/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Alert, Box, useTheme, Button, Modal, Fade, TextField, Backdrop, Grid, CircularProgress, Stack } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Effect, ModalBase } from "../../utils/Interfaces";
import { StateMapping, STATES_MAPPING } from "../../utils/Constants";
import { createTechwiseOffice } from "./techwiseOfficesFunctions";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { delayedAction } from "../../utils/Functions";
import { useAppDispatch } from "../../app/hooks";

export interface AddStorageLocationModalProps extends ModalBase {
    onTechwiseOfficeCreated: Effect;
}

const CreateStorageLocation = (props: AddStorageLocationModalProps) => {
    const { open, handleClose, onTechwiseOfficeCreated } = props;
    const theme = useTheme();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [name, setName] = useState<string>("");
    const [streetAddress, setStreetAddress] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [state, setState] = useState<StateMapping>(STATES_MAPPING[46]);
    const [zipCode, setZipCode] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setName("");
            setStreetAddress("");
            setCity("");
            setState(STATES_MAPPING[46]);
            setZipCode("");
            setLoading(false);
        });
    }

    function getFormErrors(): string {
        if (name.trim() === "") {
            return "Name cannot be blank.";
        }

        if (streetAddress.trim() === "") {
            return "Street Address cannot be blank.";
        }

        if (city.trim() === "") {
            return "City cannot be blank.";
        }

        if (zipCode.trim() === "") {
            return "Zip Code cannot be blank.";
        }

        let zipCodeNumber = parseInt(zipCode);

        if (zipCodeNumber < 10000 || zipCodeNumber >= 100000) {
            return "Zip Code cannot be lower than 10,000 or greater than 100,000.";
        }

        return "";
    }

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={onClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                        }}
                    >
                        <Alert severity={getFormErrors() === "" ? "success" : "error"} sx={{ mb: 2 }}>
                            {getFormErrors() === "" ? "All changes are valid." : getFormErrors()}
                        </Alert>

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="name"
                            label="Name"
                            name="name"
                            value={name}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="street_address"
                            label="Street Address"
                            name="street_address"
                            value={streetAddress}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setStreetAddress(event.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="city"
                            label="City"
                            name="city"
                            value={city}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCity(event.target.value)}
                        />

                        <Grid spacing={3} container>
                            <Grid item xs={6}>
                                <Autocomplete
                                    disablePortal
                                    onChange={(_event, value) => {
                                        setState(value ? value : STATES_MAPPING[46]);
                                    }}
                                    sx={{ mt: 2 }}
                                    id="state"
                                    getOptionKey={(option: StateMapping) => option.abbrev}
                                    options={STATES_MAPPING}
                                    value={state}
                                    getOptionLabel={(option: StateMapping) => option.name}
                                    renderInput={(params) => <TextField required {...params} label="State" />}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    type="number"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="zip_code"
                                    label="Zip Code"
                                    name="zip_code"
                                    value={zipCode}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setZipCode(event.target.value)}
                                />
                            </Grid>
                        </Grid>

                        {loading && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 4, mb: 3 }}>
                                <CircularProgress size={20} />
                            </Stack>
                        )}

                        {!loading && (
                            <Button
                                onClick={() => {
                                    setLoading(true);
                                    createTechwiseOffice(
                                        {
                                            name: name,
                                            street_address: streetAddress,
                                            city: city,
                                            state: state.abbrev,
                                            zip_code: parseInt(zipCode),
                                        },
                                        dispatch,
                                        () => {
                                            onTechwiseOfficeCreated();
                                            onClose();
                                        }
                                    );
                                }}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                color="primary"
                                disabled={getFormErrors() !== ""}
                            >
                                Submit
                            </Button>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default CreateStorageLocation;
